<template>
  <v-row>
    <v-col cols="12" lg="12">
      <Aggregate
        title="Projects"
        v-model="computedProjects"
        :error-messages="$utils.getIfExists(errors, ['input', 'projects'])"
        :ItemConstructor="CaseModel.Project"
        :headers="[
          { text: 'Name', value: 'name' },
          { text: 'Active', value: 'active' },
        ]"
        search-bar
        :search-text.sync="searchText"
        auto-expand-search-result
        @change="$emit('change')"
      >
        <template #[`item.active`]="{ item }">
          <v-switch v-model="item.active" label="Active" @change="$emit('change')"></v-switch>
        </template>
        <template #default="{ item, errors }">
          <v-row dense class="pa-2">
            <v-col cols="12" md="9">
              <v-list-item-title class="text-subtitle-1 font-weight-medium">
                Resources
              </v-list-item-title>
              <v-card outlined>
                <v-tabs v-model="activeTab" vertical shrink color="primary">
                  <v-tooltip v-for="tab in resourcesTabs" :key="`tab-${tab.title}`" top>
                    <template #activator="{ on }">
                      <v-tab v-on="on">
                        <template v-if="tab.icons">
                          <v-icon v-for="icon in tab.icons" size="24" :key="icon">
                            {{ icon }}
                          </v-icon>
                        </template>
                        <v-icon v-else left size="28">{{ tab.icon }}</v-icon>
                      </v-tab>
                    </template>
                    <span>{{ tab.title }}</span>
                  </v-tooltip>
                  <v-tab-item>
                    <SystemProjectDemand
                      v-model="item.demands"
                      :num_years="num_years"
                      :year_start="settings.year_start"
                      @change="$emit('change')"
                    ></SystemProjectDemand>
                  </v-tab-item>
                  <v-tab-item>
                    <SystemInverter
                      v-model="item.inverters"
                      :num-years="num_years"
                      :year-start="settings.year_start"
                      @change="$emit('change')"
                    ></SystemInverter>
                  </v-tab-item>
                  <v-tab-item>
                    <SystemProjectGenerator
                      v-model="item.generators"
                      :num_years="num_years"
                      :year_start="settings.year_start"
                      :demands="item.demands"
                      :reserves="item.reserves"
                      :generators="item.generators"
                      @change="$emit('change')"
                    ></SystemProjectGenerator>
                  </v-tab-item>
                  <v-tab-item>
                    <SystemProjectStorage
                      v-model="item.storages"
                      :num_years="num_years"
                      :year_start="settings.year_start"
                      :project="item"
                      :demands="item.demands"
                      :reserves="item.reserves"
                      :generators="item.generators"
                      @change="$emit('change')"
                    ></SystemProjectStorage>
                  </v-tab-item>
                  <v-tab-item>
                    <SystemProjectMarket
                      v-model="item.markets"
                      :num_years="num_years"
                      :year_start="settings.year_start"
                      :reserves="item.reserves"
                      :errors="errors"
                      :project-coordinates="item.coordinates"
                      @change="$emit('change')"
                    ></SystemProjectMarket>
                  </v-tab-item>
                </v-tabs>
              </v-card>
              <SystemProjectReserve
                v-model="item.reserves"
                :project="item"
                :num_years="num_years"
                :errors="errors"
                class="mt-4"
                @change="$emit('change')"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-list-item-title class="text-subtitle-1 font-weight-medium">
                Project Info
              </v-list-item-title>
              <div>
                <v-card flat outlined class="mb-2">
                  <v-responsive :aspect-ratio="1 / 1">
                    <ProjectDiagram :network="diagramNetwork(item)" />
                  </v-responsive>
                </v-card>
              </div>
              <v-card flat outlined class="pa-1 mb-2">
                <InputGeo
                  v-model="item.coordinates"
                  label="Coordinates"
                  hide-label
                  column-layout
                  responsive
                ></InputGeo>
                <InputMultiSelect
                  v-model="item.layer"
                  :items.sync="layers"
                  label="Layer"
                  :label-size="4"
                  :multiple="false"
                  dense
                  @change="$emit('change')"
                ></InputMultiSelect>
              </v-card>
              <v-card flat outlined class="pa-1">
                <p class="text-subtitle-1 font-weight-medium">Grid Properties</p>
                <InputTextField
                  v-model.number="item.xR"
                  :label="schema.xR.title"
                  type="number"
                  :error-messages="$utils.getIfExists(errors, ['xR', 'messages'])"
                  @change="$emit('change')"
                ></InputTextField>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </Aggregate>
    </v-col>
  </v-row>
</template>

<script>
import { CaseModel, schemas } from "data-dictionary";

const tabs = [
  {
    title: "Demand",
    icon: "mdi-home-city",
    href: "demand",
    resourceType: "demand",
  },
  {
    title: "Inverters",
    icon: "$inverter",
    itemsName: "inverter",
    href: "inverters",
    resourceType: "inverter",
  },
  {
    title: "Generators",
    icons: ["$pv", "$thermal", "mdi-wind-turbine"],
    itemsName: "pvItems",
    generatorType: "renewable-pv",
    href: "pv-gen",
    resourceType: "generator",
  },
  {
    title: "Storages",
    icon: "$batteryOutline",
    itemsName: "storage",
    href: "storages",
    resourceType: "storage",
  },
  {
    title: "Markets",
    icon: "mdi-finance",
    href: "markets",
    resourceType: "market",
  },
];

export default {
  name: "SystemProject",
  props: {
    num_years: {
      type: Number,
    },
    input: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      required: false,
    },
    settings: {
      type: Object,
      required: true,
    },
  },
  components: {
    Aggregate: () => import("@/components/Aggregate.vue"),
    InputGeo: () => import("@/components/InputGeo.vue"),
    InputMultiSelect: () => import("@/components/InputMultiSelect.vue"),
    InputTextField: () => import("@/components/inputs/InputTextField.vue"),
    ProjectDiagram: () => import("@/components/ProjectDiagram.vue"),
    SystemInverter: () => import("@/components/SystemInverter.vue"),
    SystemProjectDemand: () => import("@/components/SystemProjectDemand.vue"),
    SystemProjectGenerator: () => import("@/components/SystemProjectGenerator.vue"),
    SystemProjectMarket: () => import("@/components/SystemProjectMarket.vue"),
    SystemProjectReserve: () => import("@/components/SystemProjectReserve.vue"),
    SystemProjectStorage: () => import("@/components/SystemProjectStorage.vue"),
  },
  data() {
    return {
      CaseModel,
      layers: [],
      searchText: "",
      activeTab: "",
      schema: schemas.find((v) => v.$id === "project.schema.json").properties,
    };
  },
  computed: {
    computedProjects: {
      get() {
        return this.input.projects;
      },
      set(val) {
        const inputCopy = this.input;
        inputCopy.projects = val;
        this.$emit("update:input", inputCopy);
        this.$emit("change");
      },
    },
    resourcesTabs() {
      return tabs.map((t) => ({
        ...t,
        props: {
          title: t.title,
          resourceType: t.resourceType,
        },
      }));
    },
    renewable() {
      return this.input.generators.filter((e) => ["RENEWABLE", "HYDRO"].includes(e.type));
    },
    projectSettings() {
      return this.settings.STACKED_SERVICES_EMULATOR;
    },
    sequencingIsActive() {
      return this.projectSettings.sequencing && this.projectSettings.sequencing.active;
    },
  },
  mounted() {
    // get query from route
    if (this.$route.query && this.$route.query.projectName) {
      this.searchText = this.$route.query.projectName;
    }
    // Set Layers Items
    const projectsLayers = this.input.projects.length
      ? this.input.projects.map((p) => p.layer).filter((v) => v)
      : [];
    this.layers = [...new Set(projectsLayers.map((l) => l.text))].map((layerName) => ({
      text: layerName,
      color: projectsLayers.find((l) => l.text === layerName).color,
    }));
  },
  methods: {
    diagramNetwork(project) {
      return {
        bus: [{ id: project.id, name: project.name }],
        inverters: project.inverters
          ? project.inverters.map((v) => ({
              id: `inv-${v.id}`,
              name: v.name,
              busId: project.id,
              breaker: v.active ? "close" : "open",
            }))
          : [],
        storages: project.storages.map((s) => ({
          id: s.id,
          busId: s.inverter_id ? `inv-${s.inverter_id}` : project.id,
          name: s.name,
          breaker: s.active ? "close" : "open",
        })),
        generators: project.generators.map((g) => ({
          id: g.id,
          busId: project.id,
          name: g.name,
          breaker: g.active ? "close" : "open",
          type: g.type === "THERMAL" ? "THERMAL" : g.source,
        })),
        loads: project.demands.map((d) => ({
          id: d.id,
          busId: project.id,
          name: d.name,
          breaker: d.active ? "close" : "open",
        })),
        markets: project.markets.map((m) => ({
          id: m.id,
          busId: project.id,
          name: m.name,
          breaker: m.energy ? "close" : "open",
        })),
        switches: [],
      };
    },
  },
};
</script>

<style></style>
